/**
 * Checklist Template Mock Data
 *
 * Pre-defined checklist templates for various audit types.
 * Based on MHMS 2020 (Malaysian Halal Management System) requirements.
 */

import type { ChecklistItem } from '../hooks';

/**
 * Checklist template type
 */
export enum ChecklistTemplateType {
  PRE_AUDIT = 'PRE_AUDIT',             // Pre-audit checklist
  OFFICIAL_AUDIT = 'OFFICIAL_AUDIT',   // Official MHMS audit
  SURVEILLANCE = 'SURVEILLANCE',       // Surveillance audit
  DOCUMENT_REVIEW = 'DOCUMENT_REVIEW', // Document verification
  FACILITY_INSPECTION = 'FACILITY_INSPECTION', // Facility inspection
  INTERNAL_AUDIT = 'INTERNAL_AUDIT',   // Internal company audit
}

/**
 * Checklist template
 */
export interface ChecklistTemplate {
  id: string;
  name: string;
  type: ChecklistTemplateType;
  description: string;
  version: string;
  effectiveDate: Date;
  items: ChecklistItem[];
  estimatedDuration: number; // minutes
  requiredFor: string[]; // Application types or purposes
  createdBy: string;
  createdAt: Date;
}

/**
 * MHMS 2020 Official Audit Checklist Template
 * Based on Malaysian Halal Management System requirements
 */
const mhms2020AuditItems: ChecklistItem[] = [
  // Category 1: Management Responsibility
  {
    id: 'mhms-1.1',
    label: 'Top management demonstrates commitment to halal policy',
    description: 'Evidence of management commitment through documented policy and resource allocation',
    category: 'Management Responsibility',
    required: true,
    reference: 'MHMS 2020 - 4.2.1',
  },
  {
    id: 'mhms-1.2',
    label: 'Halal policy is documented and communicated',
    description: 'Halal policy is available, current, and communicated to all relevant parties',
    category: 'Management Responsibility',
    required: true,
    reference: 'MHMS 2020 - 4.2.2',
  },
  {
    id: 'mhms-1.3',
    label: 'Halal Executive appointed with authority',
    description: 'Muslim Halal Executive with appropriate authority and resources appointed',
    category: 'Management Responsibility',
    required: true,
    reference: 'MHMS 2020 - 4.2.3',
  },
  {
    id: 'mhms-1.4',
    label: 'Halal Committee established and active',
    description: 'Internal Halal Committee formed with clear roles and regular meetings',
    category: 'Management Responsibility',
    required: true,
    reference: 'MHMS 2020 - 4.2.4',
  },
  {
    id: 'mhms-1.5',
    label: 'Management review conducted regularly',
    description: 'Management reviews halal management system at planned intervals',
    category: 'Management Responsibility',
    required: true,
    reference: 'MHMS 2020 - 4.2.5',
  },

  // Category 2: Halal Management System
  {
    id: 'mhms-2.1',
    label: 'Halal Manual documented and maintained',
    description: 'Comprehensive halal manual covering all aspects of halal management',
    category: 'Halal Management System',
    required: true,
    reference: 'MHMS 2020 - 4.3.1',
  },
  {
    id: 'mhms-2.2',
    label: 'Halal objectives established and monitored',
    description: 'Measurable halal objectives set and progress monitored',
    category: 'Halal Management System',
    required: true,
    reference: 'MHMS 2020 - 4.3.2',
  },
  {
    id: 'mhms-2.3',
    label: 'Document control system implemented',
    description: 'Documented procedures for document and record control',
    category: 'Halal Management System',
    required: true,
    reference: 'MHMS 2020 - 4.3.3',
  },
  {
    id: 'mhms-2.4',
    label: 'Internal audit program established',
    description: 'Planned internal audit schedule with trained auditors',
    category: 'Halal Management System',
    required: true,
    reference: 'MHMS 2020 - 4.3.4',
  },
  {
    id: 'mhms-2.5',
    label: 'Corrective and preventive action procedures',
    description: 'System for handling non-conformities and preventing recurrence',
    category: 'Halal Management System',
    required: true,
    reference: 'MHMS 2020 - 4.3.5',
  },

  // Category 3: Premises and Equipment
  {
    id: 'mhms-3.1',
    label: 'Premises suitable for halal production',
    description: 'Facility layout prevents cross-contamination with non-halal products',
    category: 'Premises and Equipment',
    required: true,
    reference: 'MHMS 2020 - 4.4.1',
  },
  {
    id: 'mhms-3.2',
    label: 'Equipment dedicated for halal use',
    description: 'Production equipment exclusively for halal products or properly cleaned',
    category: 'Premises and Equipment',
    required: true,
    reference: 'MHMS 2020 - 4.4.2',
  },
  {
    id: 'mhms-3.3',
    label: 'Cleaning and sanitation program',
    description: 'Documented cleaning procedures and schedules maintained',
    category: 'Premises and Equipment',
    required: true,
    reference: 'MHMS 2020 - 4.4.3',
  },
  {
    id: 'mhms-3.4',
    label: 'Storage facilities segregated',
    description: 'Halal materials stored separately from non-halal materials',
    category: 'Premises and Equipment',
    required: true,
    reference: 'MHMS 2020 - 4.4.4',
  },

  // Category 4: Raw Materials and Ingredients
  {
    id: 'mhms-4.1',
    label: 'All ingredients verified as halal',
    description: 'Halal certificates or declarations for all raw materials maintained',
    category: 'Raw Materials',
    required: true,
    reference: 'MHMS 2020 - 4.5.1',
  },
  {
    id: 'mhms-4.2',
    label: 'Supplier approval system established',
    description: 'Approved supplier list with halal status verification',
    category: 'Raw Materials',
    required: true,
    reference: 'MHMS 2020 - 4.5.2',
  },
  {
    id: 'mhms-4.3',
    label: 'Incoming material inspection',
    description: 'Verification of halal status upon receipt of materials',
    category: 'Raw Materials',
    required: true,
    reference: 'MHMS 2020 - 4.5.3',
  },
  {
    id: 'mhms-4.4',
    label: 'Material specifications documented',
    description: 'Complete specifications including halal requirements for all materials',
    category: 'Raw Materials',
    required: true,
    reference: 'MHMS 2020 - 4.5.4',
  },

  // Category 5: Production Process
  {
    id: 'mhms-5.1',
    label: 'Production process prevents contamination',
    description: 'Process flow ensures no cross-contamination with non-halal products',
    category: 'Production Process',
    required: true,
    reference: 'MHMS 2020 - 4.6.1',
  },
  {
    id: 'mhms-5.2',
    label: 'Process monitoring and control',
    description: 'Critical control points identified and monitored',
    category: 'Production Process',
    required: true,
    reference: 'MHMS 2020 - 4.6.2',
  },
  {
    id: 'mhms-5.3',
    label: 'Production records maintained',
    description: 'Complete batch records with halal-relevant information',
    category: 'Production Process',
    required: true,
    reference: 'MHMS 2020 - 4.6.3',
  },

  // Category 6: Packaging and Labeling
  {
    id: 'mhms-6.1',
    label: 'Packaging materials are halal-compliant',
    description: 'All packaging materials verified as halal or halal-suitable',
    category: 'Packaging and Labeling',
    required: true,
    reference: 'MHMS 2020 - 4.7.1',
  },
  {
    id: 'mhms-6.2',
    label: 'Halal logo usage approved',
    description: 'Proper authorization for halal logo/marking on products',
    category: 'Packaging and Labeling',
    required: true,
    reference: 'MHMS 2020 - 4.7.2',
  },
  {
    id: 'mhms-6.3',
    label: 'Label information accurate',
    description: 'Product labels contain correct and complete halal information',
    category: 'Packaging and Labeling',
    required: true,
    reference: 'MHMS 2020 - 4.7.3',
  },

  // Category 7: Traceability
  {
    id: 'mhms-7.1',
    label: 'Traceability system established',
    description: 'Forward and backward traceability for all products and ingredients',
    category: 'Traceability',
    required: true,
    reference: 'MHMS 2020 - 4.8.1',
  },
  {
    id: 'mhms-7.2',
    label: 'Lot/batch identification system',
    description: 'Unique identification for each production batch',
    category: 'Traceability',
    required: true,
    reference: 'MHMS 2020 - 4.8.2',
  },
  {
    id: 'mhms-7.3',
    label: 'Traceability records retained',
    description: 'Traceability records maintained for required period',
    category: 'Traceability',
    required: true,
    reference: 'MHMS 2020 - 4.8.3',
  },

  // Category 8: Training and Competence
  {
    id: 'mhms-8.1',
    label: 'Halal training program established',
    description: 'Comprehensive halal awareness training for all staff',
    category: 'Training and Competence',
    required: true,
    reference: 'MHMS 2020 - 4.9.1',
  },
  {
    id: 'mhms-8.2',
    label: 'Training records maintained',
    description: 'Complete records of all halal-related training',
    category: 'Training and Competence',
    required: true,
    reference: 'MHMS 2020 - 4.9.2',
  },
  {
    id: 'mhms-8.3',
    label: 'Competency assessment conducted',
    description: 'Regular evaluation of staff competency in halal requirements',
    category: 'Training and Competence',
    required: true,
    reference: 'MHMS 2020 - 4.9.3',
  },
];

/**
 * Pre-Audit Checklist Template
 */
const preAuditItems: ChecklistItem[] = [
  {
    id: 'pre-1.1',
    label: 'Company registration documents available',
    description: 'Valid business registration (SSM) and relevant licenses',
    category: 'Documentation Review',
    required: true,
  },
  {
    id: 'pre-1.2',
    label: 'Halal policy statement prepared',
    description: 'Top management commitment to halal documented',
    category: 'Documentation Review',
    required: true,
  },
  {
    id: 'pre-1.3',
    label: 'Organization chart shows halal responsibility',
    description: 'Clear reporting lines for halal management',
    category: 'Documentation Review',
    required: true,
  },
  {
    id: 'pre-1.4',
    label: 'Product formulations documented',
    description: 'Complete ingredient lists for all products',
    category: 'Documentation Review',
    required: true,
  },
  {
    id: 'pre-1.5',
    label: 'Supplier halal certificates collected',
    description: 'Valid halal certificates from all suppliers',
    category: 'Documentation Review',
    required: true,
  },
  {
    id: 'pre-2.1',
    label: 'Facility layout plan available',
    description: 'Current floor plan showing production flow',
    category: 'Facility Readiness',
    required: true,
  },
  {
    id: 'pre-2.2',
    label: 'Production equipment list prepared',
    description: 'List of all equipment with halal dedication status',
    category: 'Facility Readiness',
    required: false,
  },
  {
    id: 'pre-2.3',
    label: 'Storage areas organized',
    description: 'Clear segregation between halal and non-halal materials (if applicable)',
    category: 'Facility Readiness',
    required: true,
  },
  {
    id: 'pre-3.1',
    label: 'Halal Executive identified',
    description: 'Muslim staff appointed as Halal Executive',
    category: 'Personnel',
    required: true,
  },
  {
    id: 'pre-3.2',
    label: 'Halal Committee formed',
    description: 'Internal Halal Committee with defined roles',
    category: 'Personnel',
    required: true,
  },
  {
    id: 'pre-3.3',
    label: 'Training plan developed',
    description: 'Plan for halal awareness training for all staff',
    category: 'Personnel',
    required: true,
  },
];

/**
 * Document Review Checklist Template
 */
const documentReviewItems: ChecklistItem[] = [
  {
    id: 'doc-1',
    label: 'Business license valid and current',
    category: 'Legal Documents',
    required: true,
  },
  {
    id: 'doc-2',
    label: 'Halal policy signed by top management',
    category: 'Policy Documents',
    required: true,
  },
  {
    id: 'doc-3',
    label: 'Facility layout plan approved',
    category: 'Technical Documents',
    required: true,
  },
  {
    id: 'doc-4',
    label: 'Process flow diagram complete',
    category: 'Technical Documents',
    required: true,
  },
  {
    id: 'doc-5',
    label: 'Ingredient specifications verified',
    category: 'Product Documents',
    required: true,
  },
  {
    id: 'doc-6',
    label: 'Supplier halal certificates valid',
    category: 'Supplier Documents',
    required: true,
  },
  {
    id: 'doc-7',
    label: 'Training certificates available',
    category: 'Training Documents',
    required: true,
  },
  {
    id: 'doc-8',
    label: 'SOP manual reviewed',
    category: 'Operational Documents',
    required: true,
  },
];

/**
 * Mock checklist templates
 */
export const mockChecklistTemplates: ChecklistTemplate[] = [
  {
    id: 'template-001',
    name: 'MHMS 2020 Official Audit Checklist',
    type: 'OFFICIAL_AUDIT' as ChecklistTemplateType,
    description: 'Comprehensive MHMS 2020 compliance audit checklist covering all major requirements',
    version: '2020.1',
    effectiveDate: new Date('2020-07-01'),
    items: mhms2020AuditItems,
    estimatedDuration: 480, // 8 hours
    requiredFor: ['NEW_CERTIFICATION', 'RE_CERTIFICATION'],
    createdBy: 'JAKIM',
    createdAt: new Date('2020-06-01'),
  },
  {
    id: 'template-002',
    name: 'Pre-Audit Readiness Checklist',
    type: 'PRE_AUDIT' as ChecklistTemplateType,
    description: 'Pre-audit checklist to assess company readiness for official halal audit',
    version: '1.0',
    effectiveDate: new Date('2024-01-01'),
    items: preAuditItems,
    estimatedDuration: 180, // 3 hours
    requiredFor: ['NEW_CERTIFICATION'],
    createdBy: 'MAIS Selangor',
    createdAt: new Date('2024-01-01'),
  },
  {
    id: 'template-003',
    name: 'Document Verification Checklist',
    type: 'DOCUMENT_REVIEW' as ChecklistTemplateType,
    description: 'Checklist for verifying completeness and validity of application documents',
    version: '1.2',
    effectiveDate: new Date('2024-06-01'),
    items: documentReviewItems,
    estimatedDuration: 120, // 2 hours
    requiredFor: ['NEW_CERTIFICATION', 'RENEWAL', 'MODIFICATION'],
    createdBy: 'MAIS Selangor',
    createdAt: new Date('2024-06-01'),
  },
];

/**
 * Helper functions
 */

export function getChecklistTemplateById(id: string): ChecklistTemplate | undefined {
  return mockChecklistTemplates.find((template) => template.id === id);
}

export function getChecklistTemplatesByType(type: ChecklistTemplateType): ChecklistTemplate[] {
  return mockChecklistTemplates.filter((template) => template.type === type);
}

export function getChecklistTemplateByName(name: string): ChecklistTemplate | undefined {
  return mockChecklistTemplates.find((template) => template.name === name);
}

export function getChecklistItemsByCategory(templateId: string, category: string): ChecklistItem[] {
  const template = getChecklistTemplateById(templateId);
  if (!template) return [];
  return template.items.filter((item) => item.category === category);
}

export function getRequiredItems(templateId: string): ChecklistItem[] {
  const template = getChecklistTemplateById(templateId);
  if (!template) return [];
  return template.items.filter((item) => item.required);
}

export function getCategoriesForTemplate(templateId: string): string[] {
  const template = getChecklistTemplateById(templateId);
  if (!template) return [];

  const categories = new Set(template.items.map((item) => item.category).filter(Boolean) as string[]);
  return Array.from(categories);
}

export function getTemplateStatistics(templateId: string) {
  const template = getChecklistTemplateById(templateId);
  if (!template) return null;

  const totalItems = template.items.length;
  const requiredItems = getRequiredItems(templateId).length;
  const categories = getCategoriesForTemplate(templateId);

  return {
    totalItems,
    requiredItems,
    optionalItems: totalItems - requiredItems,
    categoryCount: categories.length,
    categories,
    estimatedDuration: template.estimatedDuration,
  };
}
